import { Badge } from "@/components/ui/badge";
import { ExternalLink, Camera, Video, ClipboardCheck } from "lucide-react";
import type { ProofStandard } from "@/hooks/useProofStandards";
import { useResolvedStorageUrls } from "@/hooks/useResolvedStorageUrls";
import { isVideoMediaUrl } from "@/lib/mediaUrl";
import { cn } from "@/lib/utils";

export function unitItemTypeLabel(t: (key: string) => string, type: ProofStandard["type"]): string {
  switch (type) {
    case "proof_photo":
      return t("unitItemsDetailTypePhoto");
    case "proof_video":
      return t("unitItemsDetailTypeVideo");
    case "functional_check":
      return t("unitItemsDetailTypeFunctional");
    default:
      return type;
  }
}

/** Icon + label so cleaners always see whether the host expects photo, video, or functional proof. */
export function UnitItemProofTypeBadge({
  type,
  t,
  className,
  iconClassName,
}: {
  type: ProofStandard["type"];
  t: (key: string) => string;
  className?: string;
  iconClassName?: string;
}) {
  const Icon = type === "proof_video" ? Video : type === "functional_check" ? ClipboardCheck : Camera;
  const label = unitItemTypeLabel(t, type);
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border bg-muted/50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground",
        className,
      )}
      title={label}
    >
      <Icon className={cn("h-3.5 w-3.5 shrink-0", iconClassName)} aria-hidden />
      <span>{label}</span>
    </span>
  );
}

function defaultIsVideoUrl(url: string): boolean {
  return isVideoMediaUrl(url);
}

export function ProofStandardHostDetailContent({
  standard,
  t,
  isVideoUrl = defaultIsVideoUrl,
}: {
  standard: ProofStandard;
  t: (key: string) => string;
  isVideoUrl?: (url: string) => boolean;
}) {
  const refs = standard.reference_media_urls ?? [];
  const resolvedRefs = useResolvedStorageUrls(refs);
  const youtube = standard.reference_youtube_url?.trim() ?? "";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Badge variant={standard.required_every_turnover ? "default" : "secondary"}>
          {standard.required_every_turnover ? t("unitItemsRequiredBadge") : t("unitItemsOptionalBadge")}
        </Badge>
      </div>
      <div className="rounded-md border border-primary/20 bg-primary/5 px-3 py-2">
        <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {t("unitItemsCleanerProofRequiredLabel")}
        </p>
        <div className="mt-1.5 flex items-center gap-2">
          {standard.type === "proof_video" ? (
            <Video className="h-5 w-5 shrink-0 text-primary" aria-hidden />
          ) : standard.type === "functional_check" ? (
            <ClipboardCheck className="h-5 w-5 shrink-0 text-primary" aria-hidden />
          ) : (
            <Camera className="h-5 w-5 shrink-0 text-primary" aria-hidden />
          )}
          <span className="text-sm font-medium leading-snug">{unitItemTypeLabel(t, standard.type)}</span>
        </div>
      </div>
      {standard.room?.trim() ? (
        <p className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{t("roomLabel")}: </span>
          {standard.room.trim()}
        </p>
      ) : null}
      <div className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t("unitItemsHostWrittenNotesLabel")}
        </p>
        <p className="whitespace-pre-wrap text-sm text-foreground">
          {standard.cleaner_instructions?.trim() || t("unitItemsNoHostNotes")}
        </p>
      </div>
      <div className="space-y-2 border-t pt-3">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t("unitItemsHostReferenceMediaLabel")}
        </p>
        <p className="text-xs text-muted-foreground">{t("unitItemsHostReferenceMediaHint")}</p>
        {!youtube && refs.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("unitItemsNoReferenceMedia")}</p>
        ) : (
          <div className="space-y-3">
            {youtube ? (
              <a
                href={youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm font-medium text-primary hover:bg-primary/10 hover:underline"
              >
                <ExternalLink className="h-4 w-4 shrink-0" aria-hidden />
                {t("unitItemsWatchYoutubeDemo")}
              </a>
            ) : null}
            {resolvedRefs.length > 0 ? (
              <div className="space-y-2">
                {resolvedRefs.map((url, i) => (
                  <div key={`${refs[i] ?? url}-${i}`} className="overflow-hidden rounded-md border">
                    {isVideoUrl(url) ? (
                      <video src={url} controls className="max-h-64 w-full" />
                    ) : (
                      <img src={url} alt={`${t("unitItemsHostReferenceMediaLabel")} ${i + 1}`} className="max-h-80 w-full object-contain" />
                    )}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
